import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import { CurrentUserFirmsProps } from '@deltek/specification-client/core/Models/currentUserInfo'
import { ProjectItem } from '@deltek/specification-client/core/Models/projectItem'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { CdnClientUrl } from '@deltek/specification-client/core/Utilities/urlUtils'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import SvgIcon from '@material-ui/core/SvgIcon'
import Typography from '@material-ui/core/Typography'
import MoreVertOutlinedIcon from '@material-ui/icons/MoreVertOutlined'
import StarIcon from '@material-ui/icons/Star'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import Skeleton from '@material-ui/lab/Skeleton'
import clsx from 'clsx'
import {
    cardProjectImageDimension,
    determineProjectImage,
} from 'pages/ProjectsPage/components/projectImageUtil'
import React from 'react'
import { Link } from 'react-router-dom'
import Popout from './Popout'
import PopoutInfoContent from './PopoutInfoContent'
import ToggleIconButton from './ToggleIconButton'

const useStyles = makeStyles((theme: Theme) => ({
    projectGridItemContainer: {
        minHeight: 163,
        position: 'relative',
    },
    anchorImg: {
        height: 135,
    },
    blackBackground: {
        background: '#000000',
    },
    whiteBackground: {
        background: theme.palette.common.white,
    },
    projectGridItemBar: {
        background: theme.palette.grey[900],
        height: 28,
        width: '100%',
        bottom: 0,
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1),
        paddingRight: theme.spacing(0.5),
    },
    projectNumberBar: {
        ...theme.typography.caption,
        background: theme.palette.grey[700],
        height: 28,
        width: '100%',
        bottom: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1),
        paddingRight: theme.spacing(0.5),
        color: theme.palette.common.white,
    },
    favoriteIconButton: {
        background: 'rgba(0, 0, 0, 0.65)',
        borderRadius: 4,
        margin: theme.spacing(0.375),
        position: 'absolute',
        padding: 0,
        right: 0,
        '&:hover': {
            background: 'rgba(0,0,0, 0.75)',
        },
        top: 0,
        zIndex: 1,
    },
    projectGridItemName: {
        ...theme.typography.caption,
        cursor: 'pointer',
        textDecoration: 'none',
        zIndex: 1,
    },
    whiteFont: {
        color: theme.palette.common.white,
    },
    favoriteIcon: {
        color: theme.palette.common.white,
    },
    gridItemIcons: {
        color: '#DADADA',
    },
    noFillIcon: {
        fill: 'none !important', // Ensures even though after building, the style still persists. This gets overridden by the MUI class
    },
    slimIcon: {
        width: 15,
    },
    projectImage: {
        width: '100%',
        height: 135,
        objectFit: 'cover',
        display: 'block',
    },
    hiddenLink: {
        width: '100%',
        height: '100%',
        position: 'absolute',
        left: 0,
        zIndex: 0,
    },
    popoutContainer: {
        display: 'inline-block',
    },
    menuItem: {
        display: 'block',
        color: theme.palette.common.white,
        cursor: 'pointer',
        transition: '200ms',
        padding: theme.spacing(1, 1),
        '&:hover': {
            borderRadius: 4,
            backgroundColor: 'rgba(0,0,0,0.2)',
        },
    },
    popoutBody: {
        padding: theme.spacing(1, 2),
    },
    linkItem: {
        textDecoration: 'none',
    },
}))

export interface ProjectGridItemProps {
    project?: Partial<ProjectItem>

    /**
     * Handles clicking of favorite icon to
     * toggle favorite state.
     */
    handleFavoriteChange?: (favorite: boolean, projectId: string) => void

    /**
     * Toggles component load state
     */
    isLoading?: boolean
    /**
     * toggles the project number bar
     */
    isProjectNumberVisible?: boolean
    /**
     * opens remove project warning
     */
    openRemoveDialog?: (projectId: string | undefined) => void
    /**
     * Role check
     */
    isAdmin?: boolean
    activeFirmInfo: CurrentUserFirmsProps
}

const ProjectGridItem: React.FC<ProjectGridItemProps> = ({
    project,
    isLoading,
    handleFavoriteChange,
    isProjectNumberVisible,
    openRemoveDialog,
    isAdmin,
    activeFirmInfo,
}) => {
    const classes = useStyles()
    const [
        projectMenuAnchor,
        setProjectMenuAnchor,
    ] = React.useState<null | Element>(null)
    const [
        projectPopoutAnchor,
        setProjectPopoutAnchor,
    ] = React.useState<null | Element>(null)
    const dashboardPath = `/projects/${project?.id}/overview`
    const workspacePath = `/projects/${project?.id}/workspace`

    const projectImage = determineProjectImage({
        imageUrl: project!.imageUrl,
        parameters: project!.parameters,
        imageDimension: cardProjectImageDimension,
        defaultImage: `${CdnClientUrl}/placeholder_project_image.png`,
    })

    const handleFavoriteButtonClick = () => {
        handleFavoriteChange?.(!project?.isFavorite, project?.id ?? '')
    }

    const handleRemoveClick = () => {
        if (openRemoveDialog && project) {
            openRemoveDialog(project.id)
        }
    }

    const handleEyeIconClick = (event: React.MouseEvent) => {
        if (projectPopoutAnchor) {
            handlePopoutClose()
        } else {
            setProjectPopoutAnchor(event.currentTarget)
        }
    }

    const handlePopoutClose = () => {
        setProjectPopoutAnchor(null)
    }

    const handleKeyDownForIconButton = React.useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape' && projectPopoutAnchor) {
                e.preventDefault()
                handlePopoutClose()
            }
        },
        [projectPopoutAnchor, handlePopoutClose]
    )

    const handleMenuButtonClick = (event: React.MouseEvent) => {
        if (projectMenuAnchor) {
            handleProjectMenuClose()
        } else {
            setProjectMenuAnchor(event.currentTarget)
        }
    }

    const handleProjectMenuClose = () => {
        setProjectMenuAnchor(null)
    }

    if (isLoading) {
        return (
            <div
                className={clsx(
                    classes.projectGridItemContainer,
                    classes.whiteBackground,
                    'projectGridItemLoading'
                )}
            >
                <Skeleton variant="rect" width="100%" height="102px" />
                <Skeleton variant="text" width="100" height="34px" />
            </div>
        )
    }

    const isRemoveProjectShow = () => {
        if (
            activeFirmInfo?.firmRole === 'Admin' ||
            (activeFirmInfo?.firmRole === 'Author' && activeFirmInfo?.isPrimary)
        ) {
            return (
                <GridItem
                    onClick={handleRemoveClick}
                    data-testid="btnRemoveProject"
                    tabIndex={0}
                >
                    <Typography className={classes.menuItem} variant="caption">
                        Remove Project
                    </Typography>
                </GridItem>
            )
        } else return null
    }

    return (
        <div
            className={clsx(
                classes.projectGridItemContainer,
                classes.blackBackground,
                'projectGridItem'
            )}
        >
            <Link
                to={workspacePath}
                className={clsx(classes.anchorImg, classes.linkItem)}
                data-testid="btnIMGRedirectProjectPage"
            >
                <img
                    src={projectImage}
                    className={clsx('projectImage', classes.projectImage)}
                    alt={project?.name}
                />
            </Link>
            <ToggleIconButton
                className={clsx(
                    'projectFavoriteToggleButton',
                    classes.favoriteIconButton
                )}
                value={project?.isFavorite}
                onClick={handleFavoriteButtonClick}
                onIcon={<StarIcon className={classes.whiteFont} />}
                offIcon={<StarBorderIcon className={classes.whiteFont} />}
            />
            <div className={clsx(classes.projectGridItemBar)}>
                <Link
                    to={dashboardPath}
                    className={classes.linkItem}
                    data-testid="btnItemBarRedirectProjectPage"
                >
                    <span className={classes.hiddenLink}></span>
                    <div
                        className={clsx(
                            classes.projectGridItemName,
                            classes.whiteFont
                        )}
                    >
                        <TooltipEllipsis text={project?.name} width={180} />
                    </div>
                </Link>
                <div>
                    <IconButton
                        size="small"
                        onClick={handleEyeIconClick}
                        onKeyDown={handleKeyDownForIconButton}
                        data-testid="btnProjectInfo"
                        aria-label="project info button"
                    >
                        <SvgIcon
                            viewBox="0 0 16 16"
                            fontSize="small"
                            className={clsx(
                                'projectInfoPopoutAnchor',
                                classes.gridItemIcons,
                                classes.noFillIcon
                            )}
                        >
                            <path
                                d="M11.3333 2H4.66659C3.93021 2 3.33325 2.59695 3.33325 3.33333V12.6667C3.33325 13.403 3.93021 14 4.66659 14H11.3333C12.0696 14 12.6666 13.403 12.6666 12.6667V3.33333C12.6666 2.59695 12.0696 2 11.3333 2Z"
                                stroke="currentcolor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M6 4.66667H10"
                                stroke="currentcolor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M6 7.33333H10"
                                stroke="currentcolor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M6 10H8.66667"
                                stroke="currentcolor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </SvgIcon>
                    </IconButton>
                    <Popout
                        offset="100"
                        placement="right"
                        className="projectPopoutInfo"
                        anchorEl={projectPopoutAnchor}
                        onCloseRequest={handlePopoutClose}
                        theme="dark"
                    >
                        <PopoutInfoContent projectItem={project} />
                    </Popout>
                    <IconButton
                        size="small"
                        onClick={handleMenuButtonClick}
                        className={classes.slimIcon}
                        data-testid="btnProjectPopout"
                        aria-label="project popout button"
                    >
                        <MoreVertOutlinedIcon
                            fontSize="small"
                            className={clsx(
                                'projectMenuAnchor',
                                classes.gridItemIcons
                            )}
                        />
                    </IconButton>
                    <Popout
                        anchorEl={projectMenuAnchor}
                        onCloseRequest={handleProjectMenuClose}
                        className={clsx(
                            classes.popoutBody,
                            'projectMenuPopout'
                        )}
                        theme="dark"
                    >
                        <GridContainer direction="column">
                            <GridItem>
                                <Link
                                    to={dashboardPath}
                                    className={clsx(
                                        classes.menuItem,
                                        classes.linkItem
                                    )}
                                    data-testid="btnGoToDashboard"
                                >
                                    <Typography variant="caption">
                                        Go to Dashboard
                                    </Typography>
                                </Link>
                            </GridItem>
                            <GridItem>
                                <Link
                                    to={workspacePath}
                                    className={clsx(
                                        classes.menuItem,
                                        classes.linkItem
                                    )}
                                    data-testid="btnGoToWorkspace"
                                >
                                    <Typography variant="caption">
                                        Go to Workspace
                                    </Typography>
                                </Link>
                            </GridItem>
                            {isRemoveProjectShow()}
                        </GridContainer>
                    </Popout>
                </div>
            </div>
            {isProjectNumberVisible && (
                <div className={classes.projectNumberBar}>
                    <TooltipEllipsis
                        text={`Project #: ${project?.number}`}
                        width={250}
                    />
                </div>
            )}
        </div>
    )
}
export default ProjectGridItem
